import { action, Action, computed, Computed, thunk, Thunk } from 'easy-peasy';
import { _extendPausableModel, _PausableModel } from '../_pausable/store';
import { BaseModel, createBaseModel } from '../base-store';
import { DataModel } from '../data-store';
import { Injections } from '../types';
import { Enriched } from '../../types';
import { API } from '@xbto/api-client';
import { factories, getApiErrorMessage } from '../../utils';
import { processMarketsData, trackMarketsData } from './utils';

export interface GainersLosersModel extends BaseModel, _PausableModel {
  _data: API.ListAssetDetailsResponse[] | null;
  _setData: Action<GainersLosersModel, API.ListAssetDetailsResponse[] | null>;

  data: Computed<
    GainersLosersModel,
    Enriched.MarketDataRow[] | null | null,
    DataModel
  >;

  getData: Thunk<
    GainersLosersModel,
    | {
        type: 'gainers' | 'losers';
      }
    | undefined,
    Injections,
    DataModel
  >;
  startAggregatorTracking: Thunk<
    GainersLosersModel,
    undefined,
    Injections,
    DataModel
  >;
}

export const gainersLosersModel: GainersLosersModel = {
  ...createBaseModel(),
  ..._extendPausableModel(),
  _data: null,
  _setData: action((state, payload) => {
    state._data = payload;
  }),
  data: computed([s => s._data], _marketsData => {
    if (!_marketsData) {
      return null;
    }
    return factories.enrichListAssetDetails(_marketsData || []);
  }),
  getData: thunk(
    async (
      actions,
      payload,
      { injections: { apiClient, marketsDataAggregator } }
    ) => {
      try {
        actions.setBusy(true);

        /**
         * Note:
         * Was on a call with Augusto & BE has to implement the `Top` category filter which they wwill soon
         * THe below payload is a work around in the interim
         *
         */
        const body: API.MarketListAssetsRequest =
          payload?.type === 'gainers'
            ? {
                types: [API.MarketAssetType.Crypto],
                categories: [API.MarketAssetCategory.Top],
                sortDirection: API.SortDirection.Descending,
                sortColumn: API.MarketListAssetsSortType.PercentageChange,
                search: null,
                page: 1,
                pageSize: 3,
              }
            : {
                types: [API.MarketAssetType.Crypto],
                categories: [API.MarketAssetCategory.Top],
                sortDirection: API.SortDirection.Ascending,
                sortColumn: API.MarketListAssetsSortType.PercentageChange,
                search: null,
                page: 1,
                pageSize: 3,
              };
        const listResponse = await apiClient.listAssets(body);

        if (listResponse.errorCode || !listResponse.isSuccessful) {
          actions.setError(listResponse.errorMessage);
          actions.setBusy(false);
          return;
        }

        const { result, errorMessage } = await processMarketsData(
          listResponse.result,
          marketsDataAggregator,
          apiClient
        );

        if (errorMessage) {
          actions.setError(errorMessage);
          actions.setBusy(false);
          return;
        }

        actions._setData(result);
        actions.setBusy(false);
      } catch (error) {
        const message = getApiErrorMessage(error);
        actions.setError(message);
        actions.setBusy(false);
      }
    }
  ),
  startAggregatorTracking: thunk(
    (
      actions,
      _payload,
      { injections: { marketsDataAggregator }, getState }
    ) => {
      trackMarketsData(
        marketsDataAggregator,
        () => {
          const _state = getState();
          if (_state.paused) {
            return true;
          }
          if (!_state._data) {
            return true;
          }
          return false;
        },
        () => {
          return getState()._data;
        },
        data => {
          const _state = getState();
          if (!_state._data) {
            return;
          }
          actions._setData(data);
        }
      );
    }
  ),
};
